import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import type { UseMutationResult } from '@tanstack/react-query';
import { PlayIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react';
import {
  Controller,
  useFieldArray,
  useForm,
  type Control,
  type FieldErrors,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ContextVariablesPanel from '@/components/atoms/ContextVariables/ContextVariables';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { BAD_EXECUTION_TIME, MODERATE_EXECUTION_TIME } from '@/util/constants';
import { toContextVariables } from '@/util/contextVariables';

import CallErrorAlert from './CallErrorAlert';
import JsonBlock from './JsonBlock';
import {
  buildToolArguments,
  buildToolContext,
  CONTEXT_PLACEHOLDER,
  getDefaultFormValues,
  getSchemaFields,
  getToolErrorMessage,
  validateContext,
  validateField,
  type FieldErrorKey,
  type SchemaField,
  type TestFormValues,
} from './schemaFields';
import type { McpServer, McpTool, ToolCallRequest, ToolCallResponse } from '../hooks/useMcpServers';

export type CallToolMutation = UseMutationResult<ToolCallResponse, Error, ToolCallRequest>;

/** Field types whose control is a real input, so react-hook-form can focus it. */
const FOCUSABLE_TYPES: SchemaField['type'][] = ['string', 'number', 'integer', 'object', 'array'];

/**
 * The design rings a focused field in the brand blue (#1D4ED8 = `accent`), the same blue as the
 * switch and the Reset / Add array buttons. The shared controls default to `ring-primary`
 * (#1964CC), so it is overridden per control here rather than in `ui/constants.tsx`, which would
 * repaint the focus ring of every input in the app.
 */
const FOCUS_RING = 'focus-visible:ring-accent';

const validateAll = (
  fields: SchemaField[],
  values: TestFormValues
): FieldErrors<TestFormValues> => {
  const fieldErrors: Array<{ value: { type: string; message: FieldErrorKey } }> = [];
  let hasError = false;

  fields.forEach((field, index) => {
    const error = validateField(field, values.fields[index]);
    if (error) {
      hasError = true;
      fieldErrors[index] = { value: { type: 'validate', message: error } };
    }
  });

  const contextError = validateContext(values.context);

  if (!hasError && !contextError) {
    return {};
  }

  // A sparse array of per-field errors is exactly the shape react-hook-form reads back as
  // `errors.fields[index].value`, but it is wider than the generated `FieldErrors` type.
  return {
    ...(hasError ? { fields: fieldErrors } : {}),
    ...(contextError ? { context: { type: 'validate', message: contextError } } : {}),
  } as FieldErrors<TestFormValues>;
};

/**
 * Translation keys have to be static, so the error key maps to a literal through a lookup
 * instead of being interpolated into `t()`.
 */
const useFieldErrorMessage = () => {
  const { t } = useTranslation();

  return useCallback(
    (key?: string) => {
      if (key === 'field_mandatory') {
        return t('mcp.field_mandatory');
      }
      if (key === 'wrong_input_number') {
        return t('mcp.wrong_input_number');
      }
      if (key === 'wrong_input_integer') {
        return t('mcp.wrong_input_integer');
      }
      if (key === 'wrong_input_json') {
        return t('mcp.wrong_input_json');
      }
      if (key === 'wrong_input_object') {
        return t('mcp.wrong_input_object');
      }
      if (key === 'wrong_input_boolean') {
        return t('mcp.wrong_input_boolean');
      }
      return undefined;
    },
    [t]
  );
};

type FieldLabelProps = { children: ReactNode; hasError: boolean; htmlFor: string };

/** On an invalid field only the label turns amber — the input border stays neutral. */
const FieldLabel = ({ children, hasError, htmlFor }: FieldLabelProps) => (
  <label
    htmlFor={htmlFor}
    className={cn('text-sm leading-5 font-medium', hasError ? 'text-warning' : 'text-foreground')}
  >
    {children}
  </label>
);

const FieldError = ({ id, children }: { id: string; children: string }) => (
  <span id={id} role="alert" className="text-sm leading-5 font-medium text-warning">
    {children}
  </span>
);

/** Radix rejects an empty item value, so the boolean "not set" choice carries a sentinel. */
const UNSET_VALUE = '__unset__';

type BooleanSelectProps = {
  value: string;
  onChange: (value: string) => void;
  /** Offers a way back to "not sent"; without it one pick would lock the value in for good. */
  allowUnset: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
};

/**
 * The true/false control. Shared by a `boolean` field and by the items of a `boolean` array —
 * a free-text input there would let `"TRUE"` or `"0"` through, and `coerceScalar` turns every
 * string other than `'true'` into `false`, i.e. into the opposite call.
 */
const BooleanSelect = ({ value, onChange, allowUnset, id, ...aria }: BooleanSelectProps) => {
  const { t } = useTranslation();

  return (
    <Select
      value={value === '' ? UNSET_VALUE : value}
      onValueChange={(next) => onChange(next === UNSET_VALUE ? '' : next)}
    >
      {/* Default `md` (40px): the design's combobox is taller than the 36px text inputs. */}
      <SelectTrigger id={id} className={FOCUS_RING} {...aria}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowUnset && <SelectItem value={UNSET_VALUE}>{t('mcp.not_set')}</SelectItem>}
        <SelectItem value="true">{t('common.true')}</SelectItem>
        <SelectItem value="false">{t('common.false')}</SelectItem>
      </SelectContent>
    </Select>
  );
};

const RemoveItemButton = ({ className, onClick }: { className?: string; onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-label={t('common.remove')}
      onClick={onClick}
      // 20×20 hit box, per the design.
      className={cn(
        'shrink-0 rounded p-0.5 text-foreground-muted hover:bg-highlight-background hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        className
      )}
    >
      <Trash2Icon className="size-4" />
    </button>
  );
};

type ArrayFieldProps = {
  field: SchemaField;
  index: number;
  control: Control<TestFormValues>;
  error?: string;
  controlId: string;
  messageId: string;
};

/**
 * Lives in its own component so `useFieldArray` is called unconditionally, at a fixed hook
 * position, however many array arguments a tool declares.
 */
const ArrayField = ({ field, index, control, error, controlId, messageId }: ArrayFieldProps) => {
  const { t } = useTranslation();
  const {
    fields: items,
    append,
    remove,
  } = useFieldArray({ control, name: `fields.${index}.items` });

  return (
    <div className="flex flex-col gap-2">
      {/* The label names the first item — the rest of the array shares it. */}
      <FieldLabel hasError={Boolean(error)} htmlFor={controlId}>
        {field.name}
      </FieldLabel>

      {items.map((item, itemIndex) => {
        const name = `fields.${index}.items.${itemIndex}.value` as const;
        // Only the first item carries the label's id, so the rest name themselves.
        const itemAria =
          itemIndex === 0 ? { id: controlId } : { 'aria-label': `${field.name} ${itemIndex + 1}` };

        // A boolean item gets the same true/false control a boolean field gets. "Not set" stays
        // on offer even for a required array: a blank item is dropped, and emptying every one of
        // them is what surfaces the array's own "mandatory" error.
        if (field.itemType === 'boolean') {
          return (
            <div key={item.id} className="flex items-center gap-2">
              <Controller
                control={control}
                name={name}
                render={({ field: controlled }) => (
                  <BooleanSelect
                    value={controlled.value}
                    onChange={controlled.onChange}
                    allowUnset
                    aria-describedby={error ? messageId : undefined}
                    {...itemAria}
                  />
                )}
              />
              {items.length > 1 && <RemoveItemButton onClick={() => remove(itemIndex)} />}
            </div>
          );
        }

        return (
          <div key={item.id} className="relative">
            <Controller
              control={control}
              name={name}
              render={({ field: controlled }) => (
                <Input
                  size="sm"
                  className={cn(FOCUS_RING, items.length > 1 && 'pr-9')}
                  aria-describedby={error ? messageId : undefined}
                  {...itemAria}
                  {...controlled}
                  value={controlled.value ?? ''}
                />
              )}
            />
            {items.length > 1 && (
              <RemoveItemButton
                // `right-2.5` compensates for the smaller padding so the glyph itself stays 12px
                // in from the input's right edge.
                className="absolute top-1/2 right-2.5 -translate-y-1/2"
                onClick={() => remove(itemIndex)}
              />
            )}
          </div>
        );
      })}

      {error && <FieldError id={messageId}>{error}</FieldError>}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => append({ value: '' })}
      >
        <PlusIcon />
        {t('mcp.add_array')}
      </Button>
    </div>
  );
};

type TestFieldProps = {
  field: SchemaField;
  index: number;
  form: UseFormReturn<TestFormValues>;
  error?: string;
};

const TestField = ({ field, index, form, error }: TestFieldProps) => {
  const { control } = form;
  // Only one tool panel is mounted at a time, so the position is enough to keep these unique.
  const controlId = `mcp-test-field-${index}`;
  const messageId = `${controlId}-error`;
  const name = `fields.${index}.value` as const;

  if (field.type === 'array') {
    return (
      <ArrayField
        field={field}
        index={index}
        control={control}
        error={error}
        controlId={controlId}
        messageId={messageId}
      />
    );
  }

  const renderControl = () => {
    if (field.type === 'boolean') {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: controlled }) => (
            <BooleanSelect
              id={controlId}
              value={controlled.value}
              onChange={controlled.onChange}
              allowUnset={!field.required}
              aria-describedby={error ? messageId : undefined}
            />
          )}
        />
      );
    }

    if (field.type === 'enum') {
      // Radix rejects an empty option value, so a member that stringifies to '' is dropped.
      const options = (field.enumValues ?? []).map(String).filter((option) => option !== '');
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: controlled }) => (
            <Select value={controlled.value} onValueChange={controlled.onChange}>
              {/* Default `md` (40px): the design's combobox is taller than the 36px text inputs. */}
              <SelectTrigger id={controlId} className={FOCUS_RING}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      );
    }

    if (field.type === 'object') {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field: controlled }) => (
            <Textarea
              autoSize={{ minRows: 3, maxRows: 8 }}
              className={cn('font-mono text-xs', FOCUS_RING)}
              id={controlId}
              aria-describedby={error ? messageId : undefined}
              {...controlled}
              value={controlled.value ?? ''}
            />
          )}
        />
      );
    }

    // Numbers stay text inputs on purpose: the design shows a non-numeric value typed into a
    // number argument, with "Wrong input value, type a number" underneath.
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: controlled }) => (
          <Input
            size="sm"
            id={controlId}
            className={FOCUS_RING}
            aria-describedby={error ? messageId : undefined}
            {...controlled}
            value={controlled.value ?? ''}
          />
        )}
      />
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel hasError={Boolean(error)} htmlFor={controlId}>
        {field.name}
      </FieldLabel>
      {renderControl()}
      {error && <FieldError id={messageId}>{error}</FieldError>}
    </div>
  );
};

const CONTEXT_CONTROL_ID = 'mcp-test-context';
const CONTEXT_MESSAGE_ID = `${CONTEXT_CONTROL_ID}-error`;
const CONTEXT_HINT_ID = `${CONTEXT_CONTROL_ID}-hint`;

const CONTEXT_MIN_DRAG_HEIGHT = 96;

const CONTEXT_DRAG_GUTTER = 8;

type ContextFieldProps = {
  control: Control<TestFormValues>;
  error?: string;
  columnRef: RefObject<HTMLDivElement | null>;
};

const ContextField = ({ control, error, columnRef }: ContextFieldProps) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useEffect(() => {
    const column = columnRef.current;
    const wrapper = wrapperRef.current;
    if (!column || !wrapper) {
      return;
    }
    const measure = () => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      const box = textarea.getBoundingClientRect();
      const underTheBox = wrapper.getBoundingClientRect().bottom - box.bottom;
      const room =
        column.getBoundingClientRect().bottom - box.top - underTheBox - CONTEXT_DRAG_GUTTER;
      setMaxHeight(Math.max(room, CONTEXT_MIN_DRAG_HEIGHT));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [columnRef]);

  return (
    <div ref={wrapperRef} className="flex flex-col gap-2 border-t border-stroke pt-4">
      <div className="flex flex-col gap-1">
        <FieldLabel hasError={Boolean(error)} htmlFor={CONTEXT_CONTROL_ID}>
          {t('mcp.test_context')}
        </FieldLabel>
        <p id={CONTEXT_HINT_ID} className="text-sm leading-5 text-foreground-muted">
          {t('mcp.test_context_hint')}
        </p>
      </div>
      <Controller
        control={control}
        name="context"
        render={({ field: controlled }) => (
          <Textarea
            // Both auto-sizing modes reset the height, which fights the drag.
            autoSize={false}
            rows={7}
            style={{ maxHeight }}
            placeholder={CONTEXT_PLACEHOLDER}
            className={cn(
              'field-sizing-fixed resize-y overflow-auto font-mono text-xs',
              FOCUS_RING
            )}
            id={CONTEXT_CONTROL_ID}
            aria-describedby={error ? `${CONTEXT_MESSAGE_ID} ${CONTEXT_HINT_ID}` : CONTEXT_HINT_ID}
            {...controlled}
            ref={(node) => {
              controlled.ref(node);
              textareaRef.current = node;
            }}
            value={controlled.value ?? ''}
          />
        )}
      />
      {error && <FieldError id={CONTEXT_MESSAGE_ID}>{error}</FieldError>}
    </div>
  );
};

const durationFlavour = (duration: number) => {
  if (duration < MODERATE_EXECUTION_TIME) {
    return 'successful' as const;
  }
  return duration < BAD_EXECUTION_TIME ? ('warning' as const) : ('destructive' as const);
};

type Outcome = { label: string; flavour: 'successful' | 'warning' | 'destructive' };

/** No status code: `tools/call` is JSON-RPC, so a tool error arrives over the same 200. */
const useOutcome = (response?: ToolCallResponse, error?: Error | null): Outcome => {
  const { t } = useTranslation();

  if (error || response?.success === false) {
    return { label: t('mcp.call_failed_title'), flavour: 'destructive' };
  }
  if (response?.result?.isError) {
    return { label: t('mcp.tool_error_title'), flavour: 'warning' };
  }
  return { label: t('mcp.outcome_ok'), flavour: 'successful' };
};

const ReturnedContext = ({ context }: { context: Record<string, unknown> }) => {
  const { t } = useTranslation();

  return (
    <ContextVariablesPanel
      hideRefreshButton
      tooltipText={t('mcp.returned_context_hint')}
      variables={toContextVariables(context)}
    />
  );
};

type TestResultProps = { response?: ToolCallResponse; error: Error | null; onReset: () => void };

const TestResult = ({ response, error, onReset }: TestResultProps) => {
  const { t } = useTranslation();
  const [showJson, setShowJson] = useState(false);
  const outcome = useOutcome(response, error);

  const failure = () => {
    if (error) {
      return { title: t('mcp.call_failed_title'), description: error.message };
    }
    if (response?.success === false) {
      return { title: t('mcp.call_failed_title'), description: response.error?.message ?? '' };
    }
    if (response?.result?.isError) {
      return {
        title: t('mcp.tool_error_title'),
        description: getToolErrorMessage(response.result),
      };
    }
    return null;
  };

  const problem = failure();
  const duration = response?.duration_ms;
  const returned = response?.result?.context;
  const hasReturnedContext = Boolean(returned && Object.keys(returned).length > 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <Card className="shrink-0 bg-background p-4 text-foreground shadow-none">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex-1 text-sm leading-5 font-medium">{t('common.results')}</span>
          <Badge type="secondary" flavour={outcome.flavour}>
            {outcome.label}
          </Badge>
          {duration !== undefined && (
            <Badge type="secondary" flavour={durationFlavour(duration)}>
              {t('webhook.test.execution_time')}:{' '}
              <span className="font-semibold">{duration}ms</span>
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RotateCcwIcon />
            {t('common.reset')}
          </Button>
        </div>
      </Card>

      {response?.result && (
        <Card className="shrink-0 border border-border bg-background p-4 text-foreground">
          <div className="flex items-center justify-between">
            <span className="text-sm leading-5 font-medium">{t('common.view_json')}</span>
            <Switch
              checked={showJson}
              onCheckedChange={setShowJson}
              aria-label={t('common.view_json')}
            />
          </div>
        </Card>
      )}

      {problem && <CallErrorAlert title={problem.title} description={problem.description} />}

      {showJson && response?.result && (
        <JsonBlock className="max-h-96 shrink-0">
          {JSON.stringify(response.result, null, 2)}
        </JsonBlock>
      )}

      {!showJson && hasReturnedContext && <ReturnedContext context={returned!} />}
    </div>
  );
};

type ToolTestTabProps = {
  tool: McpTool;
  server: McpServer;
  /** A tool call is a real action on the third party, so it needs the brains write permission. */
  canWrite: boolean;
  /** The Test tab is force-mounted, so it only takes focus once it is actually shown. */
  isActive: boolean;
  callToolMutation: CallToolMutation;
};

const ToolTestTab = ({ tool, server, canWrite, isActive, callToolMutation }: ToolTestTabProps) => {
  const { t } = useTranslation();
  const getErrorMessage = useFieldErrorMessage();

  const fields = useMemo(() => getSchemaFields(tool.inputSchema), [tool.inputSchema]);

  const resolver = useCallback<Resolver<TestFormValues>>(
    (values) => {
      const errors = validateAll(fields, values);
      return Object.keys(errors).length > 0 ? { values: {}, errors } : { values, errors: {} };
    },
    [fields]
  );

  const form = useForm<TestFormValues>({ defaultValues: getDefaultFormValues(fields), resolver });

  const {
    formState: { errors },
    setFocus,
  } = form;

  // The design opens the tab with the first argument focused — once per opening, not on every
  // rebuild of `fields`. A poll of the live tools/list hands `tool.inputSchema` back as a new
  // object even when nothing changed, and re-running `setFocus` then yanks the caret out of
  // whichever argument the user was typing into.
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      hasFocusedRef.current = false;
      return;
    }
    const first = fields[0];
    if (hasFocusedRef.current || !first || !FOCUSABLE_TYPES.includes(first.type)) {
      return;
    }
    hasFocusedRef.current = true;
    setFocus(first.type === 'array' ? 'fields.0.items.0.value' : 'fields.0.value');
  }, [isActive, fields, setFocus]);

  // `callToolMutation` is shared by every tool on the page, so a result only belongs to this
  // tool when the last call was made for it. That also keeps a stale result from flashing up
  // when the selection changes.
  //
  // The server id is half the check: the page is not remounted when the route moves from one
  // server to the next (it clears `liveTools` by hand for exactly that reason), so the mutation
  // survives the switch. On name alone, a same-named tool on the next server would adopt the
  // previous server's result — or sit disabled behind its still-pending call.
  const isOwnCall =
    callToolMutation.variables?.mcp_id === server.mcp_id &&
    callToolMutation.variables?.name === tool.name;
  const hasResult =
    isOwnCall && (callToolMutation.data !== undefined || callToolMutation.error !== null);

  const argumentsRef = useRef<HTMLDivElement>(null);

  const handleSubmit = form.handleSubmit((values) => {
    callToolMutation.mutate({
      mcp_id: server.mcp_id,
      brain_id: server.brain_id,
      name: tool.name,
      arguments: buildToolArguments(fields, values),
      context: buildToolContext(values.context),
    });
  });

  if (hasResult) {
    // Unmounting the form keeps its values: react-hook-form holds them itself and hands them
    // back to the inputs when they mount again, which is what "Reset" relies on.
    return (
      <TestResult
        response={callToolMutation.data}
        error={callToolMutation.error}
        onReset={callToolMutation.reset}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The fields scroll and the footer is a flex sibling, which pins the Test button to the
          bottom of the pane without taking it out of the flow.

          `px-2` is the focus ring's clearance: `ring-2 ring-offset-2` paints 4px outside the
          control, and an overflow container clips whatever crosses its edge — at `px-0` a focused
          field kept its blue ring on the top and bottom and lost both sides. `-mx-2` gives that
          padding back to the layout, so the fields stay in the same column as the heading and the
          Test button. */}
      <div
        ref={argumentsRef}
        className="-mx-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2"
      >
        {fields.length === 0 && (
          <p className="text-sm leading-5 text-foreground-muted">{t('mcp.no_arguments')}</p>
        )}
        {fields.map((field, index) => (
          <TestField
            key={field.name}
            field={field}
            index={index}
            form={form}
            error={getErrorMessage(errors.fields?.[index]?.value?.message)}
          />
        ))}

        <ContextField
          control={form.control}
          error={getErrorMessage(errors.context?.message)}
          columnRef={argumentsRef}
        />
      </div>

      <div className="flex shrink-0 justify-end">
        {/* Read-only users can explore the form but not submit — the server enforces the same
            permission on `/mcp/call`, this just keeps the UI honest about it. */}
        <Button
          type="submit"
          variant="outline-neutral"
          size="sm"
          disabled={!canWrite}
          isLoading={isOwnCall && callToolMutation.isPending}
        >
          <PlayIcon />
          {t('common.test')}
        </Button>
      </div>
    </form>
  );
};

export default ToolTestTab;
